import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCompanyAccount } from '~/data/company-account/queryKeys';

import { queryKeysCompany } from '../queryKeys';

import { deleteCompany } from './deleteCompany';
import {
  type DeleteCompanyMutationOptions,
  type DeleteCompanyVariables,
} from './types';

/**
 * Deletes a company and clears related company and company-account data.
 *
 * @param options - Additional TanStack Query mutation options.
 * @returns The delete-company mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/delete_company_vadmin_companies__company_id__delete
 */
export function useMutationDeleteCompany(
  options?: DeleteCompanyMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyId }: DeleteCompanyVariables) =>
      deleteCompany(companyId),
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
    async onSuccess(data, variables, onMutateResult, context) {
      queryClient.removeQueries({
        exact: true,
        queryKey: queryKeysCompany.getAdmin(variables.companyId),
      });
      queryClient.removeQueries({
        exact: true,
        queryKey: queryKeysCompany.getDeletionCheck({
          companyId: variables.companyId,
        }),
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeysCompany.base()],
          refetchType: 'none',
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeysCompanyAccount.base()],
          refetchType: 'none',
        }),
      ]);

      return options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    retry: false,
  });
}
